import React, { PropsWithChildren } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Box, BoxProps, IconButtonProps } from "@chakra-ui/react"
import { SwiperOptions } from "swiper"

import CarouselNavigation from "@app/components/CarouselNavigation/CarouselNavigation"
import useCarousel from "@app/hooks/useCarousel"

import "swiper/css"
import "swiper/css/scrollbar"
import "swiper/css/navigation"

type NavigationTheme = "light" | "dark"
type NavigationProps = Omit<IconButtonProps, "aria-label">

export interface TileCarouselProps extends PropsWithChildren {
  carouselSettings?: GatsbyTypes.SanityCarouselSettings
  spaceBetweenTiles?: SwiperOptions["spaceBetween"]
  navigationTheme?: NavigationTheme
  containerProps?: BoxProps
  navigationProps?: NavigationProps
  slidesPerView?: number | "auto"
  slidesPerViewMobile?: number | "auto"
}

const defaultNavigationProps: NavigationProps = { top: "40%", left: "3%", right: "3%" }

const TileCarousel = ({
  carouselSettings,
  spaceBetweenTiles = 10,
  navigationTheme = "dark",
  containerProps,
  navigationProps,
  slidesPerView = "auto",
  slidesPerViewMobile = "auto",
  children,
}: TileCarouselProps) => {
  const responsiveSpaceBetweenTiles = spaceBetweenTiles
  const { settings, prevSlideRef, nextSlideRef } = useCarousel(carouselSettings, {
    spaceBetween: responsiveSpaceBetweenTiles,
    slidesPerView: slidesPerView,
    centerInsufficientSlides: true,
    allowTouchMove: true,
    preloadImages: false,
    initialSlide: 0,
    breakpoints: {
      1200: {
        allowTouchMove: false,
      },
    },
    scrollbar: carouselSettings?.enableScrollbar,
  })
  const {
    settings: settingsMobile,
    prevSlideRef: prevSlideRefMobile,
    nextSlideRef: nextSlideRefMobile,
  } = useCarousel(carouselSettings, {
    spaceBetween: responsiveSpaceBetweenTiles,
    slidesPerView: slidesPerViewMobile,
    allowTouchMove: true,
    preloadImages: false,
    initialSlide: 0,
    breakpoints: {
      1200: {
        allowTouchMove: false,
      },
    },
    scrollbar: carouselSettings?.enableScrollbar,
  })

  const carouselNavigationProps: NavigationProps = {
    ...defaultNavigationProps,
    bg: navigationTheme === "light" ? "white" : "brand.navy.light",
    color: navigationTheme === "light" ? "black" : "white",
    ...navigationProps,
  }

  const width = React.useMemo(() => {
    return 1 / settings.slidesPerView
  }, [settings])

  const transformWidth = React.useMemo(() => {
    if (!settings.loop) return 0
    const loopSlides = Math.ceil(settings.slidesPerView)
    const widthLooped = width * loopSlides
    let ret
    if (settings.centeredSlides) {
      const centerOffset = (1 - width) / 2
      ret = widthLooped - centerOffset
    } else {
      ret = widthLooped
    }
    return `calc(${ret * -100}% - ${loopSlides * settings.spaceBetween}px)`
  }, [settings, width])

  const widthMobile = React.useMemo(() => {
    return 1 / settingsMobile.slidesPerView
  }, [settingsMobile])

  const transformWidthMobile = React.useMemo(() => {
    if (!settingsMobile.loop) return 0
    const loopSlides = Math.ceil(settingsMobile.slidesPerView)
    const widthLooped = widthMobile * loopSlides
    let ret
    if (settingsMobile.centeredSlides) {
      const centerOffset = (1 - widthMobile) / 2
      ret = widthLooped - centerOffset
    } else {
      ret = widthLooped
    }
    return `calc(${ret * -100}% - ${loopSlides * settingsMobile.spaceBetween}px)`
  }, [settingsMobile, widthMobile])

  return (
    <Box position="relative" {...containerProps}>
      <Box
        display={["none", "block"]}
        sx={{
          ".swiper-wrapper": {
            transform: `translate3d(${transformWidth}, 0px, 0px)`,
          },
          ".swiper-slide": {
            marginRight: "10px",
            width: width ? width * 100 + "%" : "auto",
          },
          ".swiper-scrollbar": {
            display: "none",
          },
        }}
      >
        <Swiper lazy {...settings}>
          {React.Children.map(children, child => (
            <SwiperSlide>{child}</SwiperSlide>
          ))}
          {carouselSettings?.enableNavigation && (
            <Box as="span" display={["none", "initial"]}>
              <CarouselNavigation ref={prevSlideRef} direction="prev" {...carouselNavigationProps} />
              <CarouselNavigation ref={nextSlideRef} direction="next" {...carouselNavigationProps} />
            </Box>
          )}
        </Swiper>
      </Box>
      <Box
        display={["block", "none"]}
        sx={{
          ".swiper-wrapper": {
            transform: `translate3d(${transformWidthMobile}%, 0px, 0px)`,
          },
          ".swiper-slide": {
            marginRight: "10px",
            width: widthMobile ? widthMobile * 100 + "%" : "auto",
          },
          ".swiper-scrollbar": {
            display: "none",
          },
        }}
      >
        <Swiper lazy {...settingsMobile}>
          {React.Children.map(children, child => (
            <SwiperSlide>{child}</SwiperSlide>
          ))}
          {carouselSettings?.enableNavigation && (
            <Box as="span" display={["none", "initial"]}>
              <CarouselNavigation ref={prevSlideRefMobile} direction="prev" {...carouselNavigationProps} />
              <CarouselNavigation ref={nextSlideRefMobile} direction="next" {...carouselNavigationProps} />
            </Box>
          )}
        </Swiper>
      </Box>
    </Box>
  )
}

export default React.memo(TileCarousel)
